const translation = {
  app: {
    name: "TrackMyTVShow",
    documentTitle: "TrackMyTVShow — Track every episode",
    skipToContent: "Skip to content",
  },
  common: {
    saving: "Saving…",
    tryAgain: "Try again",
  },
  errors: {
    ACCOUNT_CONFIRMATION_INVALID: "Enter the confirmation phrase exactly as shown.",
    ACCOUNT_DELETE_REQUIRES_SUPPORT:
      "This account has too much related data for automatic deletion. Contact support.",
    ACCOUNT_RECENT_AUTH_REQUIRED: "Sign in again before changing this account.",
    ACCOUNT_TOO_MANY_REFRESH_TOKENS:
      "This account has too many active refresh tokens. Revoke sessions and try again.",
    ACCOUNT_TOO_MANY_SESSIONS:
      "This account has too many active sessions. Revoke another session and try again.",
    ACCOUNT_TOO_MANY_VERIFICATION_CODES:
      "This account has too many verification requests. Wait and try again.",
    AVAILABILITY_CORRECTION_LIMIT_REACHED:
      "You have too many corrections waiting to be reviewed. Wait for them to be resolved.",
    AVAILABILITY_CORRECTION_NOT_FOUND: "That correction is no longer open.",
    AVAILABILITY_CORRECTION_RATE_LIMITED: "Too many corrections just now. Try again shortly.",
    AVAILABILITY_CORRECTION_TARGET_INVALID:
      "Say which listing is wrong, or name the provider that is missing.",
    AVAILABILITY_TERRITORY_INVALID: "Choose a valid two-letter country or region code.",
    AUTH_REQUIRED: "Sign in to continue.",
    AUTH_SESSION_INVALID: "This session is no longer valid. Sign in again.",
    CATALOG_SEARCH_QUERY_INVALID: "Enter at least two characters to search the catalogue.",
    CATALOG_SHOW_NOT_FOUND: "The requested show could not be found.",
    COMMENT_BODY_INVALID: "Write between 1 and 2000 characters.",
    COMMENT_NOT_FOUND: "That comment is no longer available.",
    COMMENT_REPLY_DEPTH: "Replies go one level deep; reply to the original comment.",
    MODERATION_ACCOUNT_SUSPENDED: "This account is suspended.",
    MODERATION_POSTING_SUSPENDED: "Posting is suspended for this account.",
    MODERATION_SANCTION_NOT_FOUND: "That sanction is no longer on record.",
    REPORT_EVIDENCE_TOO_LONG: "Keep the report under 1000 characters.",
    REPORT_LIMIT_REACHED: "You have too many open reports. Wait for them to be reviewed.",
    REPORT_TARGET_INVALID: "Report either a comment or an account, not both.",
    INVITE_BLOCKED: "That invitation is not available for this account.",
    INVITE_BOUNDS_INVALID: "Choose an expiry and use count within the allowed range.",
    INVITE_EXHAUSTED: "That invitation has already been used its maximum number of times.",
    INVITE_EXPIRED: "That invitation has expired.",
    INVITE_INVALID: "That invitation link is not valid.",
    INVITE_LIMIT_REACHED: "Revoke an existing invitation before creating another.",
    INVITE_SELF: "You cannot redeem your own invitation.",
    METADATA_BATCH_TOO_LARGE: "Metadata refresh batches are limited to 50 episodes.",
    METADATA_INSTALLMENT_NOT_FOUND:
      "The installment shell is missing. Restart the metadata refresh.",
    METADATA_ORDERING_LIMIT_EXCEEDED: "Episode ordering metadata exceeded its safe cleanup limit.",
    METADATA_PROVIDER_NOT_CONFIGURED:
      "The metadata provider is not configured for this deployment.",
    METADATA_REFRESH_RATE_LIMITED: "This show was refreshed a moment ago. Try again shortly.",
    METADATA_REFRESH_INCOMPLETE:
      "The metadata refresh is incomplete. Resume its installment jobs before finalizing it.",
    NOTIFICATION_DEVICE_LIMIT_REACHED:
      "This account already has the maximum number of notification devices. Remove one first.",
    NOTIFICATION_SUBSCRIPTION_NOT_FOUND: "That notification device is no longer registered.",
    NOTIFICATION_LEAD_INVALID:
      "Choose how far ahead to be notified, up to one day before a release.",
    NOTIFICATION_TIME_INVALID: "Choose a notification time in 24-hour HH:MM form.",
    PROFILE_BOOTSTRAP_FAILED: "Your profile could not be prepared. Try again.",
    PROFILE_COUNTRY_INVALID: "Choose a valid two-letter country or region code.",
    PROFILE_DISPLAY_NAME_INVALID: "Enter a display name between 1 and 80 characters.",
    PROFILE_LOCALE_INVALID: "Choose a valid formatting locale.",
    PROFILE_NOT_FOUND: "Your profile could not be found.",
    PROFILE_TIME_ZONE_INVALID: "Choose a valid time zone.",
    PROFILE_USERNAME_INVALID:
      "Use 3–24 lowercase letters, numbers, or underscores for the username.",
    PROFILE_USERNAME_TAKEN: "That username is already in use.",
    RATING_VALUE_INVALID: "Choose a rating between half a star and five stars.",
    SOCIAL_BLOCKED: "That action is not available for this account.",
    SOCIAL_FOLLOW_LIMIT_REACHED:
      "You are following the maximum number of accounts. Unfollow someone to follow another.",
    SOCIAL_REQUEST_NOT_FOUND: "That follow request is no longer available.",
    SOCIAL_SELF_ACTION: "You cannot do that to your own account.",
    SOCIAL_USER_NOT_FOUND: "That account could not be found.",
    TRACKING_BULK_LIMIT_EXCEEDED: "Choose no more than 100 episodes for one bulk action.",
    TRACKING_BULK_NOT_FOUND: "That bulk action is no longer available.",
    TRACKING_CONFLICT: "This show changed on another device. Review and apply your choice again.",
    TRACKING_EPISODE_NOT_FOUND: "That episode is no longer available for tracking.",
    TRACKING_INSTALLMENT_NOT_FOUND: "That installment is no longer available for tracking.",
    TRACKING_PROJECTION_FAILED: "Progress could not be updated safely. Try again.",
    TRACKING_SHOW_NOT_FOLLOWED: "Follow this show before turning on its new episode alerts.",
    TRACKING_SHOW_NOT_FOUND: "That show is no longer available for tracking.",
    TRACKING_SHOW_TOO_LARGE: "This show exceeds the current tracking limit.",
    TRACKING_UNDO_EXPIRED: "The undo period for that bulk action has ended.",
    TRACKING_WATCH_EVENT_NOT_FOUND: "That watch entry is no longer available to remove.",
  },
  auth: {
    kicker: "Your watch history, one place",
    title: "Sign in. Pick up where you left off.",
    description:
      "Keep episode progress, regional release times, and title preferences consistent across devices.",
    privateWorkspace: "Private tracking workspace",
    assuranceProgress: "Progress stays attached to your account.",
    assurancePrivacy: "Profiles and activity start private.",
    assurancePasswordless: "No password to create or remember.",
    signInHeading: "Continue to TrackMyTVShow",
    signInDescription: "Use a trusted account or a one-time code sent to your email.",
    signInDescriptionOauth: "Use a trusted account to continue.",
    signInDescriptionEmail: "We send a one-time code to the email address you enter.",
    providerLabel: "Sign-in providers",
    continueGoogle: "Continue with Google",
    continueApple: "Continue with Apple",
    redirecting: "Opening provider…",
    or: "or use email",
    emailLabel: "Email address",
    emailPlaceholder: "you@example.com",
    emailInvalid: "Enter a valid email address.",
    sendCode: "Send one-time code",
    sendingCode: "Sending code…",
    codeLabel: "Six-digit code",
    codeInvalid: "Enter the six-digit code from your email.",
    codeSent: "We sent a code to {email}.",
    verifyCode: "Verify and sign in",
    verifying: "Verifying…",
    useDifferentEmail: "Use a different email",
    signInError: "Sign-in could not be completed. Check the details and try again.",
    notConfiguredTitle: "Sign-in is not available yet",
    notConfigured:
      "No sign-in method has been enabled for this site. Once Google, Apple, or email sign-in is configured, you can continue from this page.",
    invitePreserved: "Your invitation is saved and will be waiting after sign-in.",
    legal: "By continuing, you agree to use this account for your personal tracking data.",
    loading: "Checking your session…",
    loadingDescription: "Confirming the account before opening your tracking workspace.",
    providersLoading: "Loading sign-in options…",
    providersLoadingDescription: "Checking which sign-in methods this site offers.",
    providers: {
      google: "Google",
      apple: "Apple",
      email: "Email code",
    },
  },
  profile: {
    preparing: "Preparing your profile…",
    preparingDescription: "Setting up the private defaults required for this workspace.",
    bootstrapErrorTitle: "Your profile could not be prepared",
    bootstrapErrorDescription:
      "Your account is signed in, but its profile is unavailable. Try again when the connection is stable.",
    displayNameLabel: "Display name",
    displayNameRequired: "Enter the name people should see.",
    displayNameTooLong: "Keep the display name to 60 characters or fewer.",
    offlineOnboardingTitle: "Onboarding needs a connection",
    offlineOnboardingDescription:
      "This deployment has no backend configured, so there is no profile to set up. Sample screens are available from the navigation.",
    usernameLabel: "Username",
    usernameHelp: "Use 3–24 lowercase letters, numbers, or underscores.",
    countryLabel: "Country or region",
    countryPlaceholder: "Choose a country or region",
    countryRequired: "Choose your country or region.",
    timeZoneLabel: "Time zone",
    timeZonePlaceholder: "Choose a time zone",
    timeZoneRequired: "Choose your time zone.",
    localeLabel: "Date and number format",
    titlePreferenceLabel: "Title display",
    titlePreferences: {
      english: {
        label: "English when available",
        example: "Frieren: Beyond Journey’s End",
      },
      native: {
        label: "Native title first",
        example: "葬送のフリーレン",
      },
      romanized: {
        label: "Romanized title first",
        example: "Sōsō no Furīren",
      },
    },
    locales: {
      enUS: "English — United States",
      enGB: "English — United Kingdom",
      enIN: "English — India",
      jaJP: "Japanese — Japan",
      koKR: "Korean — South Korea",
    },
  },
  onboarding: {
    kicker: "A precise start",
    title: "Set the details that change what you see.",
    description:
      "Country and time zone keep release information honest. You can change every choice later in Settings.",
    identityHeading: "How you appear",
    identityDescription: "Choose a stable username and the name shown in the app.",
    regionHeading: "Where and when you watch",
    regionDescription:
      "These preferences control regional availability, release dates, and time formatting.",
    titlesHeading: "How titles read",
    titlesDescription:
      "Anime and K-drama titles keep their native, romanized, and English forms regardless of this preference.",
    continue: "Save and continue",
    saveError: "Those preferences could not be saved. Check the fields and try again.",
    offline: "Connect to finish onboarding. Your current entries remain on this screen.",
    inviteWaiting:
      "Your invitation is still saved. It will be redeemed when social features are enabled.",
  },
  notifications: {
    heading: "Release notifications",
    /*
     * Lock-screen copy, rendered by the service worker.
     *
     * Here rather than inline in `notificationPayload.ts` so there is one place
     * copy lives. The worker cannot use `react-i18next` -- there is no React in
     * it -- so it reads this table directly and interpolates `{…}` itself. A
     * second language means adding a resource file, not remembering that the
     * worker composes sentences of its own.
     */
    push: {
      episodeReleased: "{label} is out",
      episodeReleasedUnnamed: "A new episode is out",
      installmentReleased: "{installment} is out",
      installmentReleasedWithCount: "{installment} is out — {count} episodes",
    },
    // Shown before any permission is requested. The browser only lets an app
    // ask once, so the reader gets the reason first.
    valueExplanation:
      "Get a notification when a new episode is released for a show you follow with its bell turned on. Nothing else is sent, and you can turn it off per device.",
    enable: "Enable on this device",
    enableFailed: "Notifications could not be enabled on this device. Try again.",
    episodeReleases: "Notify me about new episodes",
    leadLabel: "When to notify me",
    lead: {
      "0": "At the release",
      "60": "1 hour before",
      "180": "3 hours before",
      "1440": "1 day before",
    },
    leadHelp:
      "Applied to every show you follow with its bell on. A day is the furthest ahead a release is known.",
    dailyTimeLabel: "Preferred notification time",
    dailyTimeHelp:
      "Used when a release has a date but no published time. Applied in your selected time zone.",
    thisDevice: "This device",
    removeDevice: "Remove",
    // Stored as a kind, translated here. The label is written once, when a
    // device subscribes, and read for as long as it stays subscribed -- so
    // storing English would have frozen the reader's device list in whatever
    // language they happened to be using that day.
    device: {
      iphone: "iPhone",
      ipad: "iPad",
      android: "Android device",
      mac: "Mac",
      windows: "Windows PC",
      linux: "Linux device",
      unknown: "This device",
    },
    availability: {
      enabled: "Notifications are on for this device.",
      denied:
        "This browser has blocked notifications. Allow them in your browser settings, then return here.",
      "requires-install":
        "On iOS, add TrackMyTVShow to your Home Screen first. Notifications are only delivered to an installed app.",
      unsupported: "This browser cannot receive push notifications.",
      "not-configured": "Push delivery is not configured for this deployment yet.",
      available: "",
    },
  },
  settings: {
    kicker: "Account and preferences",
    title: "Settings",
    description:
      "Control how the product identifies you, formats information, and shares activity.",
    backendRequired:
      "Connect a Convex development deployment to load authenticated profile and account settings.",
    loading: "Loading settings…",
    loadingDescription: "Retrieving profile, preference, and account controls.",
    offline: "Settings are read-only while offline. Reconnect to save or manage sessions.",
    sectionNavigation: "Settings sections",
    sections: {
      profile: "Profile",
      regional: "Region and titles",
      privacy: "Privacy",
      account: "Account",
    },
    profileHeading: "Profile",
    profileDescription: "Your username is unique. Your display name can use any writing system.",
    regionalHeading: "Region, time, and titles",
    regionalDescription:
      "These choices shape release dates, provider availability, formatting, and title order.",
    privacyHeading: "Privacy",
    privacyDescription:
      "New accounts start private. Public activity never overrides spoiler controls.",
    accountHeading: "Account and sessions",
    accountDescription: "Review linked sign-in methods and remove sessions you no longer use.",
    saveProfile: "Save profile",
    savePreferences: "Save preferences",
    savePrivacy: "Save privacy",
    saved: "Saved.",
    saveError: "Changes could not be saved. Check the fields and try again.",
    profileVisibility: "Profile visibility",
    profileVisibilityHelp: "Controls whether other people can open your profile.",
    activityVisibility: "Activity visibility",
    activityVisibilityHelp: "Controls whether your tracking activity can appear to others.",
    visibility: {
      private: "Private",
      public: "Public",
    },
    email: "Account email",
    signInMethods: "Linked sign-in methods",
    otherSessions: "Other sessions",
    sessionCount: "{count, plural, one {# other device} other {# other devices}}",
    notAvailable: "Not available",
    revokeOtherSessions: "Sign out other devices",
    sessionsRevoked: "Other sessions have been signed out.",
    signOut: "Sign out on this device",
    signingOut: "Signing out…",
    accountError: "The account action did not complete. Try again.",
    deleteHeading: "Delete account",
    deleteDescription:
      "This permanently removes the account and current profile data. Type DELETE to confirm.",
    recentAuthRequired:
      "For safety, account deletion requires a sign-in from the last 15 minutes. Sign out and back in first.",
    deleteConfirmationLabel: "Type DELETE to confirm",
    deleteConfirmationError: "Type DELETE exactly to confirm account deletion.",
    exportDescription:
      "Download everything stored against this account as a JSON file. It is built in your browser and never leaves this device except to your downloads.",
    exportPrepare: "Prepare export",
    exportPreparing: "Preparing…",
    exportDownload: "Download export",
    exportTruncated: "Very large tables were cut to the first 1000 rows: {tables}",
    exportFailed: "That export could not be prepared. Try again.",
    deleteAccount: "Delete account permanently",
  },
  manifest: {
    name: "TrackMyTVShow",
    shortName: "TrackMyTV",
    description: "Track every episode and know what to watch next.",
  },
  navigation: {
    label: "Primary navigation",
    desktopLabel: "Desktop navigation",
    mobileLabel: "Mobile navigation",
    upNext: "Up Next",
    calendar: "Calendar",
    discover: "Discover",
    friends: "Friends",
    myLists: "My Lists",
    settings: "Settings",
    designSystem: "Design system",
  },
  shell: {
    environment: "Your tracking workspace",
    sidebarLabel: "Application sidebar",
    systemStatusLabel: "Application status",
    searchLabel: "Search shows",
    searchPlaceholder: "Search shows",
    // Deliberately not "Settings": a second link with that name pointing at the
    // same route would be ambiguous in a screen reader's link list.
    accountLabel: "Account",
    online: "Online",
    offline: "Offline",
    updateAvailable: "An app update is ready.",
    updateAction: "Update now",
    offlineReady: "The app shell is ready for offline use.",
    dismiss: "Dismiss",
    connectionStatusLabel: "Connection and storage status",
    offlineTitle: "You are offline",
    offlineDescription:
      "Cached screens remain available, but tracking changes require a live connection.",
    reconnectingTitle: "Reconnecting to tracking",
    reconnectingDescription:
      "Cached data remains readable. Changes will be available after the secure connection returns.",
    savingTitle: "Saving tracking change",
    savingDescription: "Keep this page open until Convex confirms the change.",
    storageUpgradeTitle: "Preparing the offline read cache",
    storageUpgradeDescription:
      "Tracking changes stay unavailable until the local cache upgrade finishes.",
    storageFullTitle: "Offline storage is full",
    storageFullDescription:
      "Your tracking still saves to the server, but this device has no room to keep a copy for offline use. Clearing site data for other sites usually frees enough.",
    storageBlockedTitle: "Close an older app tab",
    storageBlockedDescription:
      "Another TrackMyTVShow tab is using the previous cache version. Close or reload it to finish this upgrade.",
    storageErrorTitle: "The offline read cache is unavailable",
    storageErrorDescription:
      "Tracking changes are paused because this browser could not open the private cache. Reload after checking browser storage access.",
    retiredWritesLabel: "Offline tracking migration",
    retiredWritesTitle: "Older unsynced changes were removed",
    retiredWritesDescription:
      "{count, plural, one {# offline change was} other {# offline changes were}} discarded without being sent. Review your history and reapply anything that is missing while online.",
    dismissRetiredWritesNotice: "I understand",
  },
  routeStates: {
    loadingLabel: "Loading page",
    errorTitle: "This page could not be opened",
    errorDescription: "Try the page again. Cached server data has not been removed.",
  },
  home: {
    kicker: "Repository foundation",
    title: "The tracking workspace is ready to grow.",
    description:
      "Routing, internationalization, offline installation, and the Convex boundary are in place. Product features remain intentionally empty in this phase.",
    statusHeading: "Foundation status",
    routesTitle: "Hash routes",
    routesDescription: "Static-host navigation is active without server rewrites.",
    pwaTitle: "Installable shell",
    pwaUnavailable: "Service-worker caching is unavailable in this browser or development session.",
    pwaRegistering: "The app is checking whether its service worker can register.",
    pwaRegistered: "The custom service worker is registered to manage offline build assets.",
    pwaOfflineReady:
      "The custom service worker has cached the versioned build assets needed offline.",
    pwaRegistrationFailed: "The custom service worker could not be registered.",
    offlineStorageTitle: "IndexedDB boundary",
    offlineStorageDescription: "Dexie can access durable browser storage for future snapshots.",
    convexTitle: "Convex boundary",
    convexReady: "A development deployment URL is configured.",
    convexMissing: "Link a development deployment to enable the live backend.",
    checking: "Checking",
    ready: "Ready",
    setupNeeded: "Setup needed",
    registered: "Registered",
    offlineReady: "Offline ready",
    unavailable: "Unavailable",
    registrationFailed: "Registration failed",
    scopeHeading: "Deliberately not built yet",
    scopeDescription:
      "Catalogue, tracking, notifications, social activity, and availability begin in later phases after their contracts are approved.",
  },
  catalog: {
    title: "Discover",
    // One condensed line under a poster: "Anime · Series · 2026".
    cardMeta: "{category} · {format} · {year}",
    resultCount: "{count, plural, one {# show} other {# shows}}",
    scopeNavigation: "Discovery categories",
    scope: {
      trending: "Trending",
      tv: "TV",
      anime: "Anime",
      kdrama: "K-drama",
      airing: "Airing now",
      upcoming: "Upcoming",
    },
    scopeHeading: "{scope}",
    searchResultsHeading: "Results for “{query}”",
    /*
     * States what the region actually does today, which is travel in the URL.
     * It said "Availability region: IN" over a result set nothing had filtered
     * by region, and a label claiming a filter that does not exist is worse
     * than no label. `regionSaved` disappears when the evidence arrives and
     * this becomes a real filter.
     */
    resultContext: "Showing all regions",
    regionSaved: "Region {region} is saved in this link; results are not filtered by it yet.",
    filtersLabel: "Discovery filters",
    filtersTitle: "Filters",
    closeFilters: "Close filters",
    searchLabel: "Title",
    searchPlaceholder: "English, native, or romanized title",
    genreLabel: "Genre",
    genrePlaceholder: "Drama, animation, mystery…",
    yearLabel: "Year",
    yearPlaceholder: "2026",
    regionLabel: "Region",
    regionPlaceholder: "IN",
    regionHelp:
      "Region is retained in the shareable URL. Availability filtering will use verified provider evidence when that phase is connected.",
    statusLabel: "Release status",
    applyFilters: "Apply filters",
    clearFilters: "Clear",
    source: {
      cache: "Cached catalogue",
      provider: "Updated from TMDB",
      offline: "Offline snapshot",
      sample: "Sample catalogue",
    },
    freshness: "Metadata checked {date}",
    freshnessUnknown: "Metadata freshness is unavailable.",
    loading: "Loading catalogue results",
    errorTitle: "The catalogue could not be refreshed",
    errorDescription:
      "Cached results remain available when possible. Check the connection and provider configuration, then try again.",
    offlineEmptyTitle: "No catalogue snapshot is available offline",
    offlineEmptyDescription: "Reconnect once to cache discovery results on this device.",
    noSearchResults: "No matching titles",
    noDiscoveryResults: "Nothing is cached in this category",
    noResultsDescription: "Try another title or loosen the filters for {scope}.",
    openShow: "Open {title}",
    posterAlt: "Poster for {title}",
    noPoster: "Poster unavailable",
    backdropAlt: "Key art for {title}",
    noBackdrop: "Key art unavailable",
    resultsLabel: "Catalogue results",
    missingSynopsis: "No synopsis is available from the current metadata source.",
    missingEpisodeSynopsis: "No episode synopsis is available from the current metadata source.",
    categoryLabel: "Catalogue",
    formatLabel: "Format",
    updated: "Updated {date}",
    unknown: "Unknown",
    genresUnknown: "Genres unavailable",
    category: {
      tv: "TV",
      anime: "Anime",
      kdrama: "K-drama",
    },
    format: {
      series: "Series",
      "limited-series": "Limited series",
      "tv-short": "TV short",
      ona: "ONA",
      ova: "OVA",
      special: "Special",
      unknown: "Unknown format",
    },
    status: {
      all: "Any status",
      airing: "Airing",
      upcoming: "Upcoming",
      ended: "Ended",
      returning: "Returning",
      cancelled: "Cancelled",
      unknown: "Status unknown",
    },
    releasePrecision: {
      exact: "Exact time",
      date: "Date only",
      window: "Release window",
      estimated: "Estimated",
      unknown: "Unconfirmed",
    },
    breadcrumbLabel: "Catalogue breadcrumb",
    showLoadingTitle: "Loading show details",
    showLoadingDescription: "Checking the cached catalogue and provider metadata.",
    showErrorTitle: "Show details could not be refreshed",
    showErrorDescription: "The provider request failed and no complete cached record is available.",
    showMissingTitle: "Show not found",
    showMissingDescription: "This title is not present in the cached catalogue.",
    sampleShowMissingDescription:
      "This route is not part of the disconnected sample catalogue. Return to Discover to open a sample title.",
    addToLibrary: "Add to library",
    trackingUnavailable: "Library tracking begins in the next product phase.",
    refreshingDetails: "Refreshing seasons, episodes, and title variants…",
    installmentsHeading: "Seasons and installments",
    installmentsDescription:
      "Cours, parts, OVAs, ONAs, specials, and fixed runs remain distinct instead of being forced into ordinary seasons.",
    installmentsEmptyTitle: "Installment details are not cached",
    installmentsEmptyDescription:
      "Reconnect to refresh season, cour, part, special, and episode metadata.",
    installmentType: {
      season: "Season",
      cour: "Cour",
      part: "Part",
      ova: "OVA",
      ona: "ONA",
      specials: "Specials",
      "fixed-run": "Fixed run",
      unknown: "Installment",
    },
    episodeCount: "{count, plural, one {# episode} other {# episodes}}",
    episodesNotLoaded: "Episode metadata is not loaded for this installment.",
    episodesLoading: "Loading the first episodes...",
    episodesLoadingMore: "Loading more episodes...",
    episodesError: "Episodes could not be loaded. Your tracking data is safe.",
    loadMoreEpisodes: "Load more episodes",
    loadedEpisodeRange: "{loaded} of {total} episodes loaded.",
    episodesCached: "{cached} of {total} episodes cached on this device.",
    episodesOfflineEmpty: "No episodes from this installment are cached on this device.",
    episodeLedgerLabel: "Episodes in {title}",
    episodeUnknown: "Episode number unknown",
    untitledEpisode: "Untitled episode",
    releaseUnknown: "Release not announced",
    relationsHeading: "Related titles",
    relation: {
      sequel: "Sequel",
      prequel: "Prequel",
      "spin-off": "Spin-off",
      "side-story": "Side story",
      summary: "Summary",
      adaptation: "Adaptation",
      "alternate-version": "Alternate version",
      related: "Related",
    },
    showFactsLabel: "Show metadata",
    showFactsHeading: "Metadata",
    firstReleaseLabel: "First release",
    releasePrecisionLabel: "Precision",
    originLabel: "Origin",
    languageLabel: "Original language",
    genresLabel: "Genres",
    metadataSourceLabel: "Source",
    dateOnlyNotice:
      "A date-only release does not imply a local release time. Times appear only when a source provides one.",
    orderingNotice:
      "Episode numbers are display mappings. Tracking will attach to the stable episode identity.",
    episodeLoadingTitle: "Loading episode details",
    episodeLoadingDescription: "Checking stable identity and episode ordering metadata.",
    episodeMissingTitle: "Episode not found",
    episodeMissingDescription:
      "This episode is not cached. Reopen the show while connected to refresh its installments.",
    sampleEpisodeMissingDescription: "This route is not part of the disconnected sample catalogue.",
    episodeStillAlt: "Still from {title}",
    noEpisodeStill: "Episode image unavailable",
    watchActionHeading: "Watch progress",
    watchActionDescription:
      "This episode is ready for stable tracking when the progress phase is connected.",
    markWatched: "Mark watched",
    orderingsHeading: "Episode orderings",
    ordering: {
      aired: "Aired",
      absolute: "Absolute",
      streaming: "Streaming",
      dvd: "DVD",
      production: "Production",
      "story-arc": "Story arc",
    },
    episodeContextLabel: "Episode context",
    releaseHeading: "Release",
  },
  library: {
    title: "My Lists",
    unavailable: "My Lists needs a private workspace",
    backendRequired: "Connect a deployment to see the shows you follow.",
    loading: "Loading your lists…",
    resultsLabel: "Shows you follow",
    // Both numbers, always: "12 shows" alone leaves a reader who has filtered
    // wondering whether the missing ones were dropped or never there.
    showingCount:
      "Showing {visible} of {total, plural, one {# followed show} other {# followed shows}}",
    searchLabel: "Search your lists",
    searchPlaceholder: "Title, in any language",
    applySearch: "Search",
    categoryLabel: "Type",
    anyCategory: "Any type",
    statusLabel: "Status",
    anyStatus: "Any status",
    genreLabel: "Genre",
    anyGenre: "Any genre",
    progressFilterLabel: "Progress",
    anyProgress: "Any progress",
    progressState: {
      "not-started": "Not started",
      "in-progress": "In progress",
      completed: "Completed",
    },
    sortLabel: "Sort by",
    sortOrder: {
      updated: "Recent activity",
      title: "Title",
      progress: "Progress",
      year: "Year",
    },
    clearFilters: "Clear filters",
    progressLabel: "Watched episodes of {title}",
    progressValue: "{watched} of {total} watched",
    emptyTitle: "You do not follow any shows yet",
    emptyDescription:
      "Following a show collects it here and puts its releases on your calendar. Marking an episode watched does not.",
    emptyAction: "Find something to follow",
    noMatchesTitle: "No followed show matches",
    noMatchesDescription: "Clear a filter or search for a different title.",
    offlineTitle: "Your lists need a connection",
    offlineDescription:
      "Which shows you follow is stored on the server and is not kept on this device. Reconnect to read your lists.",
    truncatedTitle: "Not every followed show is listed",
    truncatedDescription:
      "This account follows more shows than one request returns. Search and filters cover only the {count} loaded here.",
  },
  tracking: {
    showHeading: "Tracking",
    showDescription:
      "Follow a show to put its releases on your calendar. Marking episodes watched is separate.",
    backendRequired: "Connect and sign in before storing private tracking history.",
    unavailable: "Unavailable",
    follow: "Follow",
    following: "Following",
    releaseAlertsOn: "Turn off new episode alerts for this show",
    releaseAlertsOff: "Get notified when a new episode is released",
    orderingLabel: "Episode numbering",
    progress: "{watched} of {total} episodes watched",
    progressPercent: "{percent}% complete",
    progressLabel: "Show completion",
    progressRebuilding: "Progress is updating from the latest episode ordering.",
    markInstallmentWatched: "Mark {installment} watched",
    unmarkInstallmentWatched: "Clear watched episodes in {installment}",
    installmentProgress: "{watched} of {total} watched",
    markEpisodeWatched: "Mark {episode} watched",
    unmarkEpisodeWatched: "Mark {episode} not watched",
    undoBulk: "Undo bulk action",
    historyHeading: "Recent history",
    watched: "Watched",
    watchRemoved: "Watch removed",
    mutationFailed:
      "This change was not confirmed by the server. Check the connection and try it again.",
    saving: "Saving change...",
    availability: {
      ready: "Tracking is ready.",
      offline: "Cached progress is read-only while this device is offline.",
      reconnecting: "Tracking changes will return after Convex reconnects.",
      "storage-opening": "Tracking changes are paused while the offline read cache is prepared.",
      "storage-blocked": "Close or reload older TrackMyTVShow tabs to finish the cache upgrade.",
      "storage-error": "Tracking changes are paused because the private read cache is unavailable.",
      unavailable: "Connect and sign in before changing private tracking history.",
    },
    watchCount: "{count, plural, one {Watched once} other {Watched # times}}",
    watchDescription: "Mark this stable episode identity watched while connected.",
    watchedDescription: "Unchecking clears every watch. Add a rewatch to count another viewing.",
    addRewatch: "Add rewatch",
    removeLatestWatch: "Remove latest",
    lastWatched: "Latest history entry: {date}",
    upNextTitle: "Up Next",
    openEpisode: "Open {title} from {show}",
    readyToWatch: "Ready to watch",
    upNextLoading: "Loading Up Next",
    upNextLoadingDescription: "Checking materialized progress and the durable device snapshot.",
    upNextEmpty: "Nothing is ready yet",
    upNextEmptyDescription: "Mark an episode watched to build an accurate next-episode queue.",
    upNextOfflineEmpty:
      "No Up Next snapshot is stored on this device. Reconnect once after tracking a show.",
    upNextUnavailable: "Up Next needs a private workspace",
  },
  pages: {
    // The placeholder copy that lived here is gone with the placeholders: every
    // destination in the navigation now has a surface behind it. Only the
    // not-found route still describes an absence.
    notFound: {
      title: "Page not found",
      description: "This route is not part of the current foundation.",
      action: "Return to Up Next",
    },
  },
  social: {
    title: "Friends",
    tabNavigation: "Friends sections",
    unavailable: "Friends need a private workspace",
    backendRequired: "Connect a deployment to follow people and see their activity.",
    loading: "Loading…",
    // The panel prints a heading and a paragraph. Both said "Loading…", which
    // read as a rendering fault rather than a state.
    loadingDescription: "Fetching the latest from your workspace.",
    loadMore: "Show more",
    loadMoreComments: "Show more comments",
    loadingMore: "Loading…",
    unknownPerson: "Someone",
    // The footnote under a name. Each list was joined for a different reason,
    // and "Since 12 Mar" alone does not say which.
    since: {
      requested: "Requested {when}",
      following: "Following since {when}",
      follower: "Following you since {when}",
      blocked: "Blocked {when}",
    },
    unknownShow: "a show",
    unknownInstallment: "a season",
    pendingCount: "{count, plural, one {# request} other {# requests}}",
    // While a page remains unread the exact number is not known here, and
    // claiming one would be a guess about how many decisions are waiting.
    pendingCountAtLeast: "{count}+ requests",
    tab: {
      feed: "Activity",
      requests: "Requests",
      following: "Following",
      followers: "Followers",
      invites: "Invitations",
      blocked: "Blocked",
    },
    inviteFrom: "{person} invited you",
    inviteMutual: "Accepting follows them and lets them follow you.",
    inviteFollow: "Accepting lets them follow you.",
    inviteExpires: "Expires {when}",
    inviteAccept: "Accept invitation",
    inviteDismiss: "Dismiss",
    inviteFailed: "That invitation could not be accepted. It may have expired.",
    inviteUnusable: "This invitation is no longer valid",
    inviteUnusableDescription: "It has expired, been used up, or been revoked.",
    inviteCreateHeading: "Invite someone",
    inviteIntentLabel: "What it grants",
    inviteIntent: {
      mutual: "Follow each other",
      follow: "They follow you",
    },
    inviteLifetimeLabel: "Expires after",
    inviteDays: "{count, plural, one {# day} other {# days}}",
    inviteUsesLabel: "How many people can use it",
    inviteUses: "{count, plural, one {# person} other {# people}}",
    inviteCreate: "Create invitation",
    inviteCreateFailed: "That invitation could not be created. Try again.",
    inviteShownOnce: "Copy this link now. It is shown once and cannot be read back.",
    inviteLinkLabel: "Invitation link",
    inviteCopy: "Copy link",
    inviteCopied: "Copied",
    inviteListHeading: "Your invitations",
    inviteRemaining: "{used} of {total} used",
    inviteRevoke: "Revoke",
    inviteRevoked: "Revoked",
    inviteSpent: "Used up",
    inviteExpired: "Expired",
    noInvites: "No invitations yet",
    noInvitesDescription: "Create a link to bring someone into your workspace.",
    accept: "Accept",
    decline: "Decline",
    follow: "Follow",
    requestToFollow: "Request to follow",
    requestPending: "Request sent",
    followsYou: "Follows you",
    muted: "Muted",
    mute: "Mute",
    unmute: "Unmute",
    block: "Block",
    muteExplanation:
      "Muting removes someone from your feed and takes nothing away from them. Blocking severs the connection both ways and hides each of you from the other.",
    blockedExplanation:
      "You blocked this account. Unblocking does not restore the follows it severed.",
    blockedActivityDescription: "Nothing is shared while a block stands.",
    privateActivity: "This activity is private",
    privateActivityDescription: "Follow this account to see what they have been watching.",
    profileActivityHeading: "Activity",
    noSuchAccount: "No account with that username",
    noSuchAccountDescription: "Check the spelling. Usernames must match exactly.",
    findAccount: "Go",
    findAccountLabel: "Find someone by username",
    findAccountPlaceholder: "username",
    findAccountNote:
      "Type the whole username. There is no browsing, so nobody can be found by accident.",
    unfollow: "Unfollow",
    removeFollower: "Remove follower",
    unblock: "Unblock",
    noRequests: "No requests waiting",
    noRequestsDescription: "Follow requests from private accounts appear here.",
    noBlocked: "No blocked accounts",
    noBlockedDescription: "Accounts you block are listed here so you can undo it.",
    noFollowers: "No followers yet",
    noFollowing: "Not following anyone yet",
    noConnectionsDescription: "Share an invitation to connect with someone.",
    emptyFeed: "Nothing here yet",
    emptyFeedDescription: "Activity from the accounts you follow will appear here.",
    activity: {
      rated: "{actor} rated an episode of {show} {stars} stars",
      commented: "{actor} commented on {show}",
      started: "{actor} started watching {show}",
      completed: "{actor} finished {show}",
      finishedInstallment: "{actor} finished {installment} of {show}",
      rewatching: "{actor} started rewatching {show}",
      spoiler: "Spoiler",
    },
    discussionHeading: "Discussion",
    discussionUnavailable: "Discussion needs a private workspace",
    discussionBackendRequired: "Connect a deployment to read and post comments about this episode.",
    commentLabel: "Add a comment",
    replyLabel: "Write a reply",
    markSpoiler: "This contains a spoiler",
    postComment: "Post comment",
    postReply: "Post reply",
    cancelReply: "Cancel reply",
    postFailed: "That comment could not be posted. Try again.",
    noComments: "No comments yet",
    noCommentsDescription: "Be the first to say something about this episode.",
    commentDeleted: "This comment was deleted.",
    commentRemoved: "This comment was removed by a moderator.",
    spoilerLabel: "Spoiler",
    revealSpoiler: "Reveal spoiler",
    edited: "edited",
    reactionsLabel: "React to this comment",
    reactionsSampled: "Counts show the first 200 reactions",
    showAllReplies: "Show all replies",
    loadMoreReplies: "Load more replies",
    reaction: {
      like: "Like ({count})",
      insightful: "Insightful ({count})",
      funny: "Funny ({count})",
    },
    reply: "Reply",
    edit: "Edit",
    editLabel: "Edit your comment",
    saveEdit: "Save changes",
    cancelEdit: "Cancel",
    delete: "Delete",
    confirmDelete: "Delete for good",
    cancelDelete: "Keep it",
    report: "Report",
    reportReason: "Reason",
    submitReport: "Send report",
    reason: {
      spoiler: "Unmarked spoiler",
      harassment: "Harassment",
      spam: "Spam",
      other: "Something else",
    },
  },
  ratings: {
    heading: "Rating",
    yourRatingLabel: "Your rating",
    // Spoken by assistive technology for each option, so it has to say the
    // value rather than describe the picture.
    optionLabel: "{stars} stars",
    clear: "Clear rating",
    average: "{average} average from {count, plural, one {# rating} other {# ratings}}",
    averageSampled: "{average} average from the first {count} ratings",
    /*
     * An unresolved query is not an unrated episode. Coercing it to "No ratings
     * yet" stated a fact about the episode while the truth was about the
     * connection -- and during an outage it stayed there.
     */
    loading: "Loading ratings…",
    noRatings: "No ratings yet",
    saveError: "That rating could not be saved. Try again.",
  },
  availability: {
    heading: "Where to watch",
    loading: "Checking availability…",
    unavailable: "Availability needs a private workspace",
    backendRequired: "Connect a deployment to see where this episode can be watched.",
    none: "No availability on record",
    noneDescription:
      "Nothing has been reported for this territory. Try another country, or report what you know.",
    noneMatchFilter: "No providers match that filter.",
    clearFilter: "Show all providers",
    territoryLabel: "Country or region",
    accessLabel: "How you watch",
    accessAll: "Any",
    scope: {
      "episode-verified": "Verified for this episode.",
      "episode-reported": "Reported for this episode by a reader, not verified.",
      "season-reported": "Reported for this season; episode availability may vary.",
      "series-reported": "Usually available for this series; episode availability may vary.",
    },
    inherited: {
      series: "For the series",
      season: "For the season",
      episode: "For this episode",
    },
    inheritedSeason: "For season {season}",
    verifiedEpisode: "This episode",
    stale: "Needs rechecking",
    communityReported: "Reported by a reader",
    lastChecked: "Last checked {when}",
    openLink: "Open {provider} on TMDB",
    access: {
      subscription: "Included with a subscription",
      free: "Free",
      ads: "Free with ads",
      rent: "Rent",
      buy: "Buy",
      broadcast: "Broadcast",
      unknown: "Terms unknown",
    },
    territory: {
      US: "United States",
      GB: "United Kingdom",
      IN: "India",
      JP: "Japan",
      KR: "South Korea",
      DE: "Germany",
      FR: "France",
      BR: "Brazil",
      AU: "Australia",
      CA: "Canada",
    },
    reportProblem: "Something here is wrong",
    correctionNotice:
      "Corrections go to a review queue. Nothing changes for other readers until one is accepted.",
    correctionKindLabel: "What is wrong",
    correctionKind: {
      "not-available": "It is not actually available here",
      "wrong-access-type": "The terms are wrong",
      "wrong-territory": "Wrong country or region",
      "missing-provider": "A provider is missing",
      "broken-link": "The link does not work",
      other: "Something else",
    },
    correctionTargetLabel: "Which listing",
    correctionProviderLabel: "Which provider is missing",
    correctionEvidenceLabel: "Anything that would help (optional)",
    correctionSubmit: "Send correction",
    correctionCancel: "Cancel",
    correctionFailed: "That correction could not be sent. Try again.",
    attribution: "Availability data provided by JustWatch, via TMDB.",
  },
  calendar: {
    title: "Calendar",
    modeNavigation: "Calendar views",
    mode: {
      agenda: "Agenda",
      week: "Week",
      month: "Month",
    },
    previous: "Previous period",
    next: "Next period",
    today: "Today",
    timeZone: "Times shown in {timeZone}",
    offlineTitle: "This period has not been loaded here",
    offlineDescription:
      "You are offline and this range was never opened on this device, so there is nothing saved to show. Periods you have already viewed remain available.",
    showingCached:
      "Showing the last saved copy of this period. Reconnect for the current schedule.",
    networkLabel: "Network",
    networkAll: "All networks",
    noReleases: "No releases",
    // Stated in words, never carried by colour alone.
    fullDrop: "{installment} · all {count, plural, one {# episode} other {# episodes}}",
    multipleEpisodes: "{count, plural, one {# episode} other {# episodes}}",
    installmentFallback: "Full run",
    dropBadge: "Full drop",
    emptyTitle: "Nothing scheduled",
    emptyDescription:
      "Releases appear here once a show you track has dated episodes in this period.",
    loading: "Loading releases",
    loadingDescription: "Checking dated episodes for the shows you track.",
    truncated: "Showing the most recently updated shows. Narrow the period to see the rest.",
    unavailable: "The calendar needs a private workspace",
    backendRequired: "Connect and sign in to see releases for the shows you track.",
  },
  designSystem: {
    label: "Foundation reference",
    title: "Design system workshop",
    description:
      "Development-only reference for production tokens, responsive patterns, component states, multilingual titles, and validated forms.",
    colorsHeading: "Semantic colors",
    colorsDescription:
      "Feature code uses these roles instead of literal colors so state meaning remains consistent.",
    typographyHeading: "Typography",
    typePage: "Page title — one clear task",
    typeSection: "Section heading — compact hierarchy",
    typeBody:
      "Body copy stays readable, calm, and precise across utility screens and media metadata.",
    typeMetadata: "S01E08 · 24 min · Date only · Updated 28 Jul 2026",
    actionsHeading: "Actions",
    primaryAction: "Primary action",
    secondaryAction: "Secondary action",
    quietAction: "Quiet action",
    dangerAction: "Destructive action",
    disabledAction: "Disabled action",
    loadingAction: "Saving…",
    iconActionLabel: "Open settings",
    fieldsHeading: "Fields",
    defaultFieldLabel: "Default text field",
    errorFieldLabel: "Field with an error",
    errorFieldValue: "A",
    errorFieldMessage: "Use at least three characters.",
    selectFieldLabel: "Title display",
    selectEnglish: "English when available",
    selectNative: "Native title first",
    selectRomanized: "Romanized title first",
    disabledFieldLabel: "Disabled field",
    disabledFieldValue: "Unavailable while offline",
    statesHeading: "System states",
    stateReady: "Ready",
    stateLoading: "Loading",
    stateEmpty: "Empty",
    stateOffline: "Offline",
    statePending: "Saving",
    stateError: "Error",
    offlineBannerTitle: "You are offline",
    offlineBannerDescription: "Cached screens remain available on this device.",
    pendingBannerTitle: "Saving tracking change",
    pendingBannerDescription: "The connected request is waiting for server confirmation.",
    errorBannerTitle: "Change not confirmed",
    errorBannerDescription: "Review the connection and submit the change again.",
    contentStatesHeading: "Content states",
    emptyTitle: "No tracked shows yet",
    emptyDescription: "Add a show from Discover to build your Up Next queue.",
    offlineTitle: "No offline snapshot",
    offlineDescription: "Reconnect once to make this screen available on this device.",
    deniedTitle: "Permission is off",
    deniedDescription: "Allow notifications in browser settings before enabling release alerts.",
    errorTitle: "This content could not be loaded",
    errorDescription: "Cached data remains untouched. Try the request again.",
    retryAction: "Try again",
    loadingHeading: "Skeleton geometry",
    loadingSpecimenLabel: "Loading media row",
    titlesHeading: "Title stress test",
    englishTitle: "The Remarkably Long Chronicle of a Quiet Television Evening",
    japaneseTitle: "銀河鉄道の終着駅で待ち続けた彼女と百年後の約束",
    japaneseRomanized:
      "Ginga Tetsudō no Shūchakueki de Machitsuzuketa Kanojo to Hyakunen-go no Yakusoku",
    koreanTitle: "이상한 변호사 우영우와 아주 오래된 봄날의 약속",
    koreanRomanized: "Isanghan Byeonhosa U Yeong-u",
    railHeading: "Contextual rail",
    railMainTitle: "Primary task area",
    railMainDescription: "The main column keeps the active tracking or inspection task in focus.",
    railLabel: "Contextual information example",
    railTitle: "Useful context",
    railDescription:
      "Desktop adds release precision, availability evidence, or account context without stretching the mobile layout.",
    formHeading: "Form foundation",
    formDescription: "TanStack Form uses Zod validation at the submission boundary.",
    displayTitleLabel: "Preferred display title",
    displayTitlePlaceholder: "Enter a title",
    displayTitleRequired: "Enter a display title.",
    submit: "Validate example",
    submitting: "Validating…",
    submitted: "The example passed validation.",
    colorNames: {
      background: "Application background",
      surface: "Base surface",
      raised: "Raised surface",
      overlay: "Overlay surface",
      action: "Primary action",
      success: "Success",
      info: "Informational",
      warning: "Warning",
      pending: "Pending sync",
      danger: "Error",
      spoiler: "Spoiler hidden",
    },
  },
};

export const en = {
  translation,
  errors: translation.errors,
  foundation: {
    app: translation.app,
    common: translation.common,
    home: translation.home,
    manifest: translation.manifest,
    navigation: translation.navigation,
    pages: translation.pages,
    routeStates: translation.routeStates,
    shell: translation.shell,
  },
  account: {
    app: translation.app,
    auth: translation.auth,
    common: translation.common,
    notifications: translation.notifications,
    onboarding: translation.onboarding,
    profile: translation.profile,
    settings: translation.settings,
    shell: translation.shell,
  },
  catalog: translation.catalog,
  designSystem: translation.designSystem,
} as const;
